import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiUser, FiSearch, FiTrendingUp, FiClock, FiCreditCard, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { getProfile } from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await getProfile();
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getScoreColor = (score) => {
    if (!score) return 'text-gray-400';
    if (score >= 700) return 'text-success-500';
    if (score >= 500) return 'text-warning-500';
    return 'text-danger-500';
  };

  const lastConsultation = history[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Meu Dashboard - ConsulteSeuNome</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-gray-900">
              Olá, {user?.name || 'Usuário'}!
            </h1>
            <p className="text-gray-600 mt-2">
              Bem-vindo ao seu dashboard
            </p>
          </motion.div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Score */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Último Score</p>
                  <p className={`text-4xl font-bold ${getScoreColor(lastConsultation?.score)}`}>
                    {lastConsultation?.score || '--'}
                  </p>
                </div>
                <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center">
                  <FiTrendingUp className="w-7 h-7 text-primary-600" />
                </div>
              </div>
            </motion.div>

            {/* Consultations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total de Consultas</p>
                  <p className="text-4xl font-bold text-gray-900">{history.length}</p>
                </div>
                <div className="w-14 h-14 bg-success-100 rounded-full flex items-center justify-center">
                  <FiSearch className="w-7 h-7 text-success-600" />
                </div>
              </div>
            </motion.div>

            {/* Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Status Atual</p>
                  <p className={`text-2xl font-bold ${lastConsultation?.has_negative ? 'text-danger-500' : 'text-success-500'}`}>
                    {lastConsultation?.has_negative === false ? 'Nome Limpo' : lastConsultation?.has_negative ? 'Com Restrições' : 'Sem Consulta'}
                  </p>
                </div>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${lastConsultation?.has_negative ? 'bg-danger-100' : 'bg-success-100'}`}>
                  {lastConsultation?.has_negative ? (
                    <FiAlertCircle className="w-7 h-7 text-danger-600" />
                  ) : (
                    <FiCreditCard className="w-7 h-7 text-success-600" />
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card mb-8"
          >
            <h2 className="text-xl font-semibold mb-4">Ações Rápidas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="/"
                className="flex items-center p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <FiSearch className="w-6 h-6 text-primary-600 mr-3" />
                <div>
                  <p className="font-semibold text-primary-700">Nova Consulta</p>
                  <p className="text-sm text-primary-600">Consulte seu CPF novamente</p>
                </div>
              </a>
              <a
                href="/blog"
                className="flex items-center p-4 bg-success-50 rounded-lg hover:bg-success-100 transition-colors"
              >
                <FiTrendingUp className="w-6 h-6 text-success-600 mr-3" />
                <div>
                  <p className="font-semibold text-success-700">Dicas para seu Score</p>
                  <p className="text-sm text-success-600">Aprenda a melhorar seu crédito</p>
                </div>
              </a>
            </div>
          </motion.div>

          {/* History */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="card"
          >
            <h2 className="text-xl font-semibold mb-4">Histórico de Consultas</h2>
            {history.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Data</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">CPF</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Score</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Total Dívida</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center text-gray-600">
                            <FiClock className="w-4 h-4 mr-2" />
                            {formatDate(item.created_at)}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-900">
                          ***.***.***{item.cpf?.slice(-4)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`font-bold ${getScoreColor(item.score)}`}>
                            {item.score}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {item.has_negative ? (
                            <span className="px-3 py-1 bg-danger-100 text-danger-600 rounded-full text-sm font-medium">
                              Com Restrições
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-success-100 text-success-600 rounded-full text-sm font-medium">
                              Limpo
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-900 font-medium">
                          {formatCurrency(item.total_debt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <FiSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nenhuma consulta realizada ainda</p>
                <a href="/" className="btn-primary inline-block mt-4">
                  Fazer primeira consulta
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
